import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router'
import type { ColumnDef } from '@tanstack/react-table'
import { Plus } from 'lucide-react'
import { DataTable, DateValue, FormDialog, PageHeader, StatusBadge, EmployeePicker } from '@/components/common'
import { Button } from '@/components/ui/button'
import { DatePicker } from '@/components/ui/date-picker'
import { Label } from '@/components/ui/label'
import { Select } from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { useBreadcrumb } from '@/hooks/useBreadcrumb'
import { useLookup } from '@/hooks/useLookup'
import { toApiProblem } from '@/lib/api/client'
import { useExitCases, useExitMutations } from '../api'
import { EXIT_STATUS_LABEL, EXIT_STATUS_TONE, EXIT_TYPE_LABEL } from '../types'
import type { ExitCaseView, ExitStatus, ExitType } from '../types'

/** Who is leaving, and how far along each case is. */
export function ExitCasesPage() {
  useBreadcrumb()
  const navigate = useNavigate()

  const [entityId, setEntityId] = useState('')
  const [status, setStatus] = useState('')
  const [exitType, setExitType] = useState('')
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(0)
  const [size, setSize] = useState(25)

  const cases = useExitCases({ entityId, status, exitType, q: search, page, size })
  const entities = useLookup<{ id: string; name: string }>('legalEntities')
  const actions = useExitMutations()

  const [opening, setOpening] = useState(false)
  const [draft, setDraft] = useState({
    employeeId: '', exitType: 'RESIGNATION', reason: '', noticeGivenOn: '', lastWorkingDay: '',
  })
  const [error, setError] = useState<string | null>(null)

  const columns = useMemo<ColumnDef<ExitCaseView, unknown>[]>(() => [
    { id: 'reference', header: 'Case', accessorFn: (row) => row.reference },
    {
      id: 'employeeName',
      header: 'Person',
      accessorFn: (row) => row.employeeName ?? '—',
      cell: ({ row }) => (
        <span>
          {row.original.employeeName ?? '—'}
          <span className="ms-2 font-mono text-xs text-[var(--color-muted)]">
            {row.original.employeeCode}
          </span>
        </span>
      ),
    },
    {
      id: 'exitType',
      header: 'Leaving because',
      accessorFn: (row) => row.exitType,
      cell: ({ row }) => EXIT_TYPE_LABEL[row.original.exitType as ExitType] ?? row.original.exitType,
    },
    {
      id: 'lastWorkingDay',
      header: 'Last day',
      accessorFn: (row) => row.lastWorkingDay,
      cell: ({ row }) => (row.original.lastWorkingDay
        ? <DateValue value={row.original.lastWorkingDay} />
        : (
          <span className="text-[var(--color-muted)]" title={row.original.noticeNote ?? ''}>
            Not set
          </span>
        )),
    },
    {
      id: 'clearance',
      header: 'Clearance',
      accessorFn: (row) => row.clearanceOutstanding,
      cell: ({ row }) => (row.original.clearanceTotal === 0
        ? <span className="text-[var(--color-muted)]">Not built</span>
        : row.original.clearanceOutstanding === 0
          ? <span className="text-[var(--color-success)]">All answered</span>
          : `${row.original.clearanceOutstanding} of ${row.original.clearanceTotal} outstanding`),
    },
    {
      id: 'status',
      header: 'Status',
      accessorFn: (row) => row.status,
      cell: ({ row }) => (
        <StatusBadge
          status={row.original.status}
          tone={EXIT_STATUS_TONE[row.original.status as ExitStatus] ?? 'neutral'}
          label={EXIT_STATUS_LABEL[row.original.status as ExitStatus] ?? row.original.status}
        />
      ),
    },
  ], [])

  return (
    <div className="space-y-4">
      <PageHeader
        title="Exits"
        subtitle="Notice, approval, clearance, settlement — in that order, with a gate in the middle."
        actions={
          <Button type="button" onClick={() => { setError(null); setOpening(true) }}>
            <Plus className="size-4" aria-hidden="true" />
            Open a case
          </Button>
        }
      />

      <DataTable<ExitCaseView>
        columns={columns}
        data={cases.data}
        isLoading={cases.isPending}
        error={cases.error}
        onRetry={() => void cases.refetch()}
        page={page}
        size={size}
        search={search}
        onPageChange={setPage}
        onSizeChange={setSize}
        onSearchChange={(value) => { setSearch(value); setPage(0) }}
        onRowClick={(row) => { void navigate(`/exit/${row.id}`) }}
        emptyTitle="Nobody is leaving"
        emptyDescription="A case holds the sequence together: notice, approval, clearance, settlement, letters."
        toolbar={
          <>
            <Select
              aria-label="Legal entity"
              value={entityId}
              onChange={(event) => { setEntityId(event.target.value); setPage(0) }}
            >
              <option value="">Every entity</option>
              {entities.data?.map((entity) => (
                <option key={entity.id} value={entity.id}>{entity.name}</option>
              ))}
            </Select>
            <Select
              aria-label="Status"
              value={status}
              onChange={(event) => { setStatus(event.target.value); setPage(0) }}
            >
              <option value="">Any status</option>
              {Object.entries(EXIT_STATUS_LABEL).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </Select>
            <Select
              aria-label="Leaving because"
              value={exitType}
              onChange={(event) => { setExitType(event.target.value); setPage(0) }}
            >
              <option value="">Any reason</option>
              {Object.entries(EXIT_TYPE_LABEL).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </Select>
          </>
        }
      />

      <FormDialog
        open={opening}
        onOpenChange={(open) => { if (!open) { setOpening(false); setError(null) } }}
        title="Open an exit case"
        description="If a notice rule matches, the leaving date is worked out from it. If none does, set it here."
        submitLabel="Open the case"
        loading={actions.openCase.isPending}
        error={error}
        onSubmit={() => {
          void (async () => {
            setError(null)
            try {
              const created = await actions.openCase.mutateAsync({
                employeeId: draft.employeeId,
                exitType: draft.exitType,
                reason: draft.reason || null,
                noticeGivenOn: draft.noticeGivenOn || null,
                lastWorkingDay: draft.lastWorkingDay || null,
                paymentInLieu: false,
              })
              setOpening(false)
              void navigate(`/exit/${created.id}`)
            } catch (caught) {
              setError(toApiProblem(caught)?.detail ?? 'That case could not be opened')
            }
          })()
        }}
      >
        <div className="space-y-3">
          <div>
            <Label htmlFor="exit-employee">Who is leaving</Label>
            <EmployeePicker
                  id="exit-employee"
                  value={draft.employeeId}
                  onChange={(employeeId) => setDraft({ ...draft, employeeId: employeeId ?? '' })}
                />
          </div>
          <div>
            <Label htmlFor="exit-type">Leaving because</Label>
            <Select
              id="exit-type" className="w-full" value={draft.exitType}
              onChange={(event) => setDraft({ ...draft, exitType: event.target.value })}
            >
              {Object.entries(EXIT_TYPE_LABEL).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </Select>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <Label htmlFor="exit-notice">Notice given on</Label>
              <DatePicker
                id="exit-notice" value={draft.noticeGivenOn}
                onChange={(event) => setDraft({ ...draft, noticeGivenOn: event.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="exit-last-day">Last working day</Label>
              <DatePicker
                id="exit-last-day" value={draft.lastWorkingDay}
                onChange={(event) => setDraft({ ...draft, lastWorkingDay: event.target.value })}
              />
              <p className="mt-1 text-xs text-[var(--color-muted)]">
                Leave it empty to let a notice rule work it out. With no rule configured nothing
                computes one, and the case says so.
              </p>
            </div>
          </div>
          <div>
            <Label htmlFor="exit-reason">Reason</Label>
            <Textarea
              id="exit-reason" value={draft.reason}
              onChange={(event) => setDraft({ ...draft, reason: event.target.value })}
            />
          </div>
        </div>
      </FormDialog>
    </div>
  )
}
